import { NotificationCenter, notify } from '@pique/component';
import { WorkspaceFlavour } from '@pique/env/workspace';
import {
  useLiveData,
  useService,
  WorkspaceService,
} from '@toeverything/infra';
import { useAtom } from 'jotai';
import type { ReactElement } from 'react';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  authAtom,
  openSettingModalAtom,
  openSignOutModalAtom,
  openTrashModalAtom,
} from '../atoms';
import { AuthModal as Auth } from '../components/pique/auth';
import { HistoryTipsModal } from '../components/pique/history-tips-modal';
import { IssueFeedbackModal } from '../components/pique/issue-feedback-modal';
import { LocalQuotaModal } from '../components/pique/quota-reached-modal';
import type { SettingProps } from '../components/pique/setting-modal';
import { SettingModal } from '../components/pique/setting-modal';
import { SignOutModal } from '../components/pique/sign-out-modal';
import { TrashModal } from '../components/pique/trash-modal';
import { MoveToTrash } from '../components/page-list';
import { useTrashModalHelper } from '../hooks/pique/use-trash-modal-helper';
import { useAsyncCallback } from '../hooks/pique-async-hooks';
import { AuthService } from '../modules/cloud/services/auth';
import { ImportTemplateDialogProvider } from '../modules/import-template';
import { PeekViewManagerModal } from '../modules/peek-view';

export const Setting = () => {
  const [{ open, workspaceMetadata, activeTab }, setOpenSettingModalAtom] =
    useAtom(openSettingModalAtom);

  const onSettingClick: SettingProps['onSettingClick'] = useCallback(
    ({ activeTab, workspaceMetadata }) => {
      setOpenSettingModalAtom(prev => ({
        ...prev,
        activeTab,
        workspaceMetadata: workspaceMetadata ?? undefined,
      }));
    },
    [setOpenSettingModalAtom]
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      setOpenSettingModalAtom(prev => ({ ...prev, open }));
    },
    [setOpenSettingModalAtom]
  );

  useEffect(() => {
    return;
  }, [setOpenSettingModalAtom]);

  if (!open) {
    return null;
  }

  return (
    <SettingModal
      open={open}
      activeTab={activeTab}
      workspaceMetadata={workspaceMetadata ?? null}
      onSettingClick={onSettingClick}
      onOpenChange={onOpenChange}
    />
  );
};

export const AuthModal = (): ReactElement => {
  const [{ openModal, state }, setAuthAtom] = useAtom(authAtom);
  const authService = useService(AuthService);
  const status = useLiveData(authService.session.status$);
  const isShareRoute = useLocation().pathname.startsWith('/share/');
  const isUnauthenticated = status === 'unauthenticated';
  const persistent = isUnauthenticated && !isShareRoute;
  const shouldShowModal = !isShareRoute && (openModal || isUnauthenticated);

  return (
    <Auth
      open={shouldShowModal}
      state={state}
      persistent={persistent}
      setOpen={useCallback(
        (open: boolean) => {
          if (!persistent) {
            setAuthAtom(prev => ({ ...prev, openModal: open }));
          }
        },
        [setAuthAtom, persistent]
      )}
      onSignedIn={useCallback(() => {
        setAuthAtom(prev => ({ ...prev, openModal: false }));
      }, [setAuthAtom])}
    />
  );
};

export function CurrentWorkspaceModals() {
  const currentWorkspace = useService(WorkspaceService).workspace;

  const { trashModal, setTrashModal, handleOnConfirm } = useTrashModalHelper(
    currentWorkspace.docCollection
  );
  const deletePageTitles = trashModal.pageTitles;
  const trashConfirmOpen = trashModal.open;
  const onTrashConfirmOpenChange = useCallback(
    (open: boolean) => {
      setTrashModal({
        ...trashModal,
        open,
      });
    },
    [trashModal, setTrashModal]
  );

  const [trashListOpen, setTrashListOpen] = useAtom(openTrashModalAtom);

  return (
    <>
      <IssueFeedbackModal />
      {currentWorkspace?.flavour === WorkspaceFlavour.LOCAL && (
        <>
          <LocalQuotaModal />
          <HistoryTipsModal />
        </>
      )}
      <PeekViewManagerModal />
      <MoveToTrash.ConfirmModal
        open={trashConfirmOpen}
        onConfirm={handleOnConfirm}
        onOpenChange={onTrashConfirmOpenChange}
        titles={deletePageTitles}
      />
      <TrashModal open={trashListOpen} onOpenChange={setTrashListOpen} />
    </>
  );
}

export const SignOutConfirmModal = () => {
  const authService = useService(AuthService);
  const [open, setOpen] = useAtom(openSignOutModalAtom);

  const onConfirm = useAsyncCallback(async () => {
    setOpen(false);
    try {
      await authService.signOut();
    } catch (err) {
      console.error(err);
      notify.error({
        title: 'Failed to sign out',
      });
    }
    // Cloud workspaces disappear from the list after sign-out.
    // The workspace page's "not found" guard handles redirect to bootstrap.
  }, [authService, setOpen]);

  return (
    <SignOutModal open={open} onOpenChange={setOpen} onConfirm={onConfirm} />
  );
};

export const AllWorkspaceModals = (): ReactElement => {
  return (
    <>
      <NotificationCenter />
      <ImportTemplateDialogProvider />
      <AuthModal />
      <SignOutConfirmModal />
      <Setting />
    </>
  );
};
